import { useCallback, useReducer } from 'react';
import { canReparentSection } from '../quoteSectionTree';
import {
  applyPartnerQuoteSettings,
  createLine,
  emptyEditor,
  prefillEditorFromAffair,
} from '../quoteEditorModel';

const updateNestedLine = (sections, lineId, updater) => sections.map((section) => ({
  ...section,
  lines: (section.lines || []).map((line) => (
    line.id === lineId ? updater(line) : line
  )),
}));

const mutateLineOrder = (sections, lineId, operation) => sections.map((section) => {
  const lines = [...(section.lines || [])];
  const index = lines.findIndex((line) => String(line.id) === String(lineId));
  if (index < 0) return section;
  const nextLines = operation(lines, index);
  return { ...section, lines: nextLines.map((line, orderIndex) => ({ ...line, orderIndex })) };
});

export const quoteEditorReducer = (state, action) => {
  const optionAction = ['option:add', 'option:update', 'option:remove', 'option:clear',
    'option:line:add', 'option:line:update', 'option:line:remove'].includes(action.type)
    || (action.type === 'update' && action.field === 'options');
  if (optionAction) {
    if (!state || state.permissions?.canEdit === false || (state.versionStatus && state.versionStatus !== 'draft')) return state;
    if (action.optionId != null) {
      const option = (state.options || []).find((item) => String(item.id) === String(action.optionId));
      if (!option) return state;
      if (action.type === 'option:line:add' && !option.sections?.[0]) return state;
      if (action.lineId != null && !(option.sections || []).some((section) =>
        (section.lines || []).some((line) => String(line.id) === String(action.lineId)))) return state;
    }
    // Only explicit option actions set intent. Reads, calculations and array
    // normalization never do; the flag is reset by editorFromQuote on reload.
    state = { ...state, optionsDirty: true };
  }
  switch (action.type) {
    case 'replace':
      return typeof action.editor === 'function' ? action.editor(state) : action.editor;
    case 'update': {
      return { ...state, [action.field]: action.value, serverCalculationStatus: 'stale' };
    }
    case 'customerSnapshot':
      return {
        ...state,
        customerSnapshot: { ...(state.customerSnapshot || {}), ...action.snapshot },
        serverCalculationStatus: 'stale',
      };
    case 'parameters': {
      const nextParameters = Array.isArray(action.value) ? action.value : [];
      const laborRates = Object.fromEntries(nextParameters
        .filter((parameter) => String(parameter.parameterCode || '').startsWith('laborRate.'))
        .map((parameter) => [String(parameter.parameterCode).slice('laborRate.'.length), parameter.valueNumber]));
      const sections = (state.sections || []).map((section) => ({
        ...section,
        lines: (section.lines || []).map((line) => {
          const code = line.laborProfileCode || line.laborItems?.[0]?.profileCodeSnapshot || '';
          if (!code || laborRates[code] === undefined || laborRates[code] === null) return line;
          const currentItem = line.laborItems?.[0] || null;
          if (currentItem?.rateMode === 'manual') return line;
          const value = laborRates[code];
          return {
            ...line,
            laborRate: value,
            laborItems: Array.isArray(line.laborItems)
              ? line.laborItems.map((item) => {
                const itemCode = item.profileCodeSnapshot || item.profileCode || code;
                const itemValue = laborRates[itemCode];
                return itemValue === undefined || itemValue === null || item.rateMode === 'manual'
                  ? item
                  : { ...item, costHourlyRateSnapshot: itemValue, effectiveHourlyRate: itemValue, rateMode: 'profile' };
              })
              : line.laborItems,
          };
        }),
      }));
      return { ...state, versionParameters: nextParameters, sections, serverCalculationStatus: 'stale' };
    }
    case 'expenses':
      return { ...state, versionExpenses: action.value, serverCalculationStatus: 'stale' };

    case 'section:add': {
      const sectionId = `section-${Date.now()}-${Math.random().toString(16).slice(2)}`;
      const parentSectionId = action.parentSectionId || null;
      const siblings = (state.sections || []).filter((section) => String(section.parentSectionId || '') === String(parentSectionId || ''));
      return {
        ...state,
        serverCalculationStatus: 'stale',
        primarySectionId: state.primarySectionId || sectionId,
        sections: [...(state.sections || []), {
          id: sectionId,
          parentSectionId,
          code: action.code || '',
          title: action.title || (parentSectionId ? 'Nouveau sous-ensemble' : 'Nouvel ensemble'),
          description: '',
          isClientVisible: true,
          orderIndex: siblings.length,
          lines: [],
        }],
      };
    }
    case 'section:update':
      if (action.field === 'parentSectionId' && !canReparentSection(state.sections || [], action.sectionId, action.value)) return state;
      return {
        ...state,
        serverCalculationStatus: 'stale',
        sections: (state.sections || []).map((section) => String(section.id) === String(action.sectionId)
          ? { ...section, [action.field]: action.value }
          : section),
      };
    case 'section:remove': {
      const doomed = new Set([String(action.sectionId)]);
      let changed = true;
      const sectionsToCheck = state.sections || [];
      while (changed) {
        changed = false;
        for (const section of sectionsToCheck) {
          if (section.parentSectionId != null && doomed.has(String(section.parentSectionId)) && !doomed.has(String(section.id))) {
            doomed.add(String(section.id)); changed = true;
          }
        }
      }
      const sections = (state.sections || []).filter((section) => !doomed.has(String(section.id)));
      return {
        ...state,
        serverCalculationStatus: 'stale',
        primarySectionId: sections.some((section) => String(section.id) === String(state.primarySectionId)) ? state.primarySectionId : sections[0]?.id || null,
        sections,
      };
    }
    case 'sections:import': {
      const imported = Array.isArray(action.sections) ? action.sections : [];
      return {
        ...state,
        serverCalculationStatus: 'stale',
        primarySectionId: action.replace ? (imported[0]?.id || null) : (state.primarySectionId || imported[0]?.id || null),
        sections: action.replace ? imported : [...(state.sections || []), ...imported],
      };
    }
    case 'text:add': {
      const requestedSectionId = action.sectionId || state.primarySectionId;
      const target = (state.sections || []).find((section) => String(section.id) === String(requestedSectionId)) || (state.sections || [])[0];
      if (!target) return state;
      const textLine = createLine({
        description: action.text ?? '',
        lineType: 'text', quantity: 0, unit: 'Ens', estimatedHours: 0, laborRate: 0,
        purchaseCost: 0, subcontractingCost: 0, marginCoefficient: 1,
        vatRate: 0, quoteSectionId: target.id, isClientVisible: true,
      });
      return {
        ...state,
        serverCalculationStatus: 'stale',
        sections: (state.sections || []).map((section) => {
          if (String(section.id) !== String(target.id)) return section;
          const nextLines = [...(section.lines || [])];
          const afterIndex = action.afterLineId == null ? -1 : nextLines.findIndex((line) => String(line.id) === String(action.afterLineId));
          if (afterIndex >= 0) nextLines.splice(afterIndex + 1, 0, textLine);
          else nextLines.push(textLine);
          return { ...section, lines: nextLines.map((line, orderIndex) => ({ ...line, orderIndex })) };
        }),
      };
    }
    case 'line:update':
      if (action.field === 'quoteSectionId') {
        let moved = null;
        const without = (state.sections || []).map((section) => ({
          ...section,
          lines: (section.lines || []).filter((line) => {
            if (String(line.id) === String(action.lineId)) { moved = { ...line, quoteSectionId: action.value }; return false; }
            return true;
          }),
        }));
        if (!moved) return state;
        return {
          ...state,
          serverCalculationStatus: 'stale',
          sections: without.map((section) => String(section.id) === String(action.value)
            ? { ...section, lines: [...(section.lines || []), moved] }
            : section),
        };
      }
      return {
        ...state,
        serverCalculationStatus: 'stale',
        sections: updateNestedLine(state.sections, action.lineId, (line) => {
          const next = { ...line, [action.field]: action.value };
          if (action.field === 'marginCoefficient') {
            next.coefficientMode = 'manual';
            next.manualCoefficient = action.value;
            next.effectiveCoefficient = action.value;
          }
          if (action.field === 'laborProfile') {
            const profile = action.value || null;
            const rate = profile?.currentRate || null;
            next.laborProfileId = profile?.id || null;
            next.laborProfileCode = profile?.code || '';
            next.laborProfileLabel = profile?.label || '';
            if (rate) next.laborRate = rate.costHourlyRate;
            next.laborItems = profile ? [{
              laborProfileId: profile.id,
              profileCodeSnapshot: profile.code,
              profileLabelSnapshot: profile.label,
              resourceQuantity: next.quantity || 1,
              unitHours: next.estimatedHours || 0,
              costHourlyRateSnapshot: rate?.costHourlyRate ?? next.laborRate ?? 0,
              sellingHourlyRateSnapshot: rate?.suggestedSellingHourlyRate ?? null,
              rateMode: 'profile',
            }] : [];
            delete next.laborProfile;
          }
          if (action.field === 'estimatedHours' && Array.isArray(next.laborItems) && next.laborItems.length === 1) {
            next.laborItems = [{ ...next.laborItems[0], unitHours: action.value, resourceQuantity: next.quantity || 1 }];
          }
          if (action.field === 'quantity' && Array.isArray(next.laborItems) && next.laborItems.length === 1) {
            next.laborItems = [{ ...next.laborItems[0], resourceQuantity: action.value || 1 }];
          }
          if (action.field === 'laborRate' && Array.isArray(next.laborItems) && next.laborItems.length === 1) {
            next.laborItems = [{ ...next.laborItems[0], costHourlyRateSnapshot: action.value, effectiveHourlyRate: action.value, rateMode: 'manual' }];
          }
          if (action.field === 'lineType' && action.value === 'material') {
            next.estimatedHours = 0;
            next.laborRate = 0;
            next.subcontractingCost = 0;
          }
          if (action.field === 'lineType' && action.value === 'subcontracting') {
            next.estimatedHours = 0;
            next.laborRate = 0;
            next.purchaseCost = 0;
          }
          return next;
        }),
      };
    case 'line:add': {
      const sections = state.sections || [];
      const requestedSectionId = action.overrides?.quoteSectionId;
      const requestedSection = sections.find((section) =>
        String(section.id) === String(requestedSectionId));
      const primarySection = sections.find((section) =>
        String(section.id) === String(state.primarySectionId));
      const targetSection = requestedSection || primarySection || sections[0];
      const sectionId = targetSection?.id || `section-${Date.now()}`;
      const line = createLine({
        description: 'Nouvelle prestation',
        lineType: 'service',
        laborRate: state.defaultLaborRate,
        marginCoefficient: state.defaultMarginCoefficient,
        vatRate: state.defaultVatRate,
        ...action.overrides,
        quoteSectionId: sectionId,
      });
      if (!targetSection) {
        return {
          ...state,
          primarySectionId: sectionId,
          serverCalculationStatus: 'stale',
          sections: [{
            id: sectionId,
            parentSectionId: null,
            title: 'Lignes du devis',
            orderIndex: 0,
            lines: [line],
          }],
        };
      }
      return {
        ...state,
        serverCalculationStatus: 'stale',
        sections: sections.map((section) => (
          String(section.id) === String(sectionId)
            ? { ...section, lines: [...(section.lines || []), line] }
            : section
        )),
      };
    }
    case 'line:duplicate':
      return {
        ...state,
        serverCalculationStatus: 'stale',
        sections: mutateLineOrder(state.sections, action.lineId, (lines, index) => {
          const source = lines[index];
          const copy = createLine({
            ...source,
            id: `line-${Date.now()}-${Math.random().toString(16).slice(2)}`,
            description: `${source.description || 'Ligne'} - copie`,
            forcedAt: null,
            forcedByUser: null,
          });
          lines.splice(index + 1, 0, copy);
          return lines;
        }),
      };
    case 'line:move':
      if (state.permissions?.canEdit === false || (state.versionStatus && state.versionStatus !== 'draft')) return state;
      return {
        ...state,
        serverCalculationStatus: 'stale',
        sections: mutateLineOrder(state.sections, action.lineId, (lines, index) => {
          const target = action.direction === 'up' ? index - 1 : index + 1;
          if (target < 0 || target >= lines.length) return lines;
          [lines[index], lines[target]] = [lines[target], lines[index]];
          return lines;
        }),
      };
    case 'line:remove':
      return {
        ...state,
        serverCalculationStatus: 'stale',
        sections: state.sections.map((section) => ({
          ...section,
          lines: (section.lines || []).filter((line) => line.id !== action.lineId),
        })),
      };
    case 'option:add': {
      const optionId = `option-${Date.now()}`;
      const sectionId = `${optionId}-section`;
      return {
        ...state,
        serverCalculationStatus: 'stale',
        options: [...(state.options || []), {
          id: optionId,
          title: 'Nouvelle option',
          selected: false,
          sections: [{
            id: sectionId,
            parentSectionId: null,
            title: 'Option',
            orderIndex: 0,
            lines: [createLine({
              description: 'Nouvelle prestation optionnelle',
              quoteSectionId: sectionId,
              vatRate: state.defaultVatRate,
            })],
          }],
        }],
      };
    }
    case 'option:update':
      return {
        ...state,
        serverCalculationStatus: 'stale',
        options: (state.options || []).map((option) => (
          String(option.id) === String(action.optionId)
            ? { ...option, [action.field]: action.value }
            : option
        )),
      };
    case 'option:clear':
      return { ...state, options: [], serverCalculationStatus: 'stale' };
    case 'option:remove':
      return {
        ...state,
        serverCalculationStatus: 'stale',
        options: (state.options || []).filter((option) => String(option.id) !== String(action.optionId)),
      };
    case 'option:line:add':
      return {
        ...state,
        serverCalculationStatus: 'stale',
        options: (state.options || []).map((option) => {
          if (String(option.id) !== String(action.optionId)) return option;
          const sections = option.sections || [];
          const target = sections[0];
          if (!target) return option;
          return {
            ...option,
            sections: sections.map((section, index) => index === 0 ? {
              ...section,
              lines: [...(section.lines || []), createLine({
                description: 'Nouvelle prestation optionnelle',
                quoteSectionId: section.id,
                vatRate: state.defaultVatRate,
              })],
            } : section),
          };
        }),
      };
    case 'option:line:update':
      return {
        ...state,
        serverCalculationStatus: 'stale',
        options: (state.options || []).map((option) => (
          String(option.id) !== String(action.optionId) ? option : {
            ...option,
            sections: updateNestedLine(option.sections || [], action.lineId, (line) => ({
              ...line,
              [action.field]: action.value,
            })),
          }
        )),
      };
    case 'option:line:remove':
      return {
        ...state,
        serverCalculationStatus: 'stale',
        options: (state.options || []).map((option) => (
          String(option.id) !== String(action.optionId) ? option : {
            ...option,
            sections: (option.sections || []).map((section) => ({
              ...section,
              lines: (section.lines || []).filter((line) => String(line.id) !== String(action.lineId)),
            })),
          }
        )),
      };
    case 'partner-terms:apply':
      return applyPartnerQuoteSettings(state, action.terms, action.companyId);
    case 'line:material:apply':
      return {
        ...state,
        serverCalculationStatus: 'stale',
        sections: updateNestedLine(state.sections, action.lineId, (line) => {
          const material = { ...action.material };
          const materials = action.replaceExisting === false
            ? [...(line.materials || []), material]
            : [material];
          return {
            ...line,
            description: action.updateDescription === false
              ? line.description
              : (material.descriptionSnapshot || line.description),
            unit: material.unit || line.unit || 'U',
            materials,
          };
        }),
      };
    default:
      return state;
  }
};

export default function useQuoteEditor({ affairs, pagePermissions }) {
  const [editor, dispatch] = useReducer(quoteEditorReducer, null);
  const setEditor = useCallback((editorValue) => {
    dispatch({ type: 'replace', editor: editorValue });
  }, []);

  const openNewEditor = useCallback((projectId, affairId) => {
    const affair = affairs.find((item) => String(item.id) === String(affairId));
    const initial = prefillEditorFromAffair(emptyEditor(projectId, affairId), affair);
    dispatch({
      type: 'replace',
      editor: {
        ...initial,
        permissions: {
          ...initial.permissions,
          canEdit: Boolean(pagePermissions.canCreate),
          canViewCosts: Boolean(pagePermissions.canViewCosts),
          canViewMinute: Boolean(pagePermissions.canViewMinute),
        },
      },
    });
  }, [affairs, pagePermissions]);

  const updateEditor = useCallback((field, value) => {
    if (field === 'affairId') {
      const affair = affairs.find((item) => String(item.id) === String(value));
      dispatch({
        type: 'replace',
        editor: prefillEditorFromAffair({
          ...editor,
          affairId: value,
          projectId: '',
          serverCalculationStatus: 'stale',
        }, affair),
      });
      return;
    }
    dispatch({ type: 'update', field, value });
  }, [affairs, editor]);

  return {
    editor,
    setEditor,
    openNewEditor,
    updateEditor,
    applyCustomerSnapshot: (snapshot) => dispatch({ type: 'customerSnapshot', snapshot }),
    applyPartnerTerms: (companyId, terms) => dispatch({ type: 'partner-terms:apply', companyId, terms }),
    updateVersionParameters: (value) => dispatch({ type: 'parameters', value }),
    updateVersionExpenses: (value) => dispatch({ type: 'expenses', value }),
    updateLine: (lineId, field, value) =>
      dispatch({ type: 'line:update', lineId, field, value }),
    applyCatalogMaterial: (lineId, material, options = {}) => dispatch({
      type: 'line:material:apply',
      lineId,
      material,
      ...options,
    }),
    addLine: (overrides = {}) => dispatch({ type: 'line:add', overrides }),
    addTextLine: (sectionId, text = '', afterLineId = null) => dispatch({ type: 'text:add', sectionId, text, afterLineId }),
    addSection: (parentSectionId = null, title = '') => dispatch({ type: 'section:add', parentSectionId, title }),
    updateSection: (sectionId, field, value) => dispatch({ type: 'section:update', sectionId, field, value }),
    removeSection: (sectionId) => dispatch({ type: 'section:remove', sectionId }),
    importSections: (sections, replace = false) => dispatch({ type: 'sections:import', sections, replace }),
    duplicateLine: (lineId) => dispatch({ type: 'line:duplicate', lineId }),
    moveLine: (lineId, direction) => dispatch({ type: 'line:move', lineId, direction }),
    removeLine: (lineId) => dispatch({ type: 'line:remove', lineId }),
    addOption: () => dispatch({ type: 'option:add' }),
    updateOption: (optionId, field, value) => dispatch({ type: 'option:update', optionId, field, value }),
    removeOption: (optionId) => dispatch({ type: 'option:remove', optionId }),
    clearOptions: () => dispatch({ type: 'option:clear' }),
    addOptionLine: (optionId) => dispatch({ type: 'option:line:add', optionId }),
    updateOptionLine: (optionId, lineId, field, value) => dispatch({ type: 'option:line:update', optionId, lineId, field, value }),
    removeOptionLine: (optionId, lineId) => dispatch({ type: 'option:line:remove', optionId, lineId }),
  };
}
